import { maxQuantityPerOrder } from '@/lib/order';

/**
 * Largest quantity offered as a preset in the dropdown. Anything above this is
 * reached by picking the `6+` option and typing the amount instead, so the list
 * stays short no matter how much stock a vendor holds.
 */
export const MAX_DROPDOWN_QUANTITY = 6;

/**
 * The most a shopper may take of a line: whatever stock allows, further capped
 * for the few pskus that carry a per-order limit.
 */
export const getMaxQuantity = (stock: number, psku?: string): number => {
  const pskuMax = psku ? maxQuantityPerOrder[psku] : undefined;

  return pskuMax ? Math.min(pskuMax, stock) : stock;
};

/** Whether the `6+` escape hatch is worth showing at all. */
export const offersCustomQuantity = (max: number): boolean =>
  Math.floor(max) > MAX_DROPDOWN_QUANTITY;

/**
 * Presets to list. When `6+` is on offer it owns the ceiling itself, so the
 * presets stop one short of it rather than showing both `6` and `6+`.
 */
export const buildQuantityOptions = (max: number): number[] => {
  const ceiling = offersCustomQuantity(max)
    ? MAX_DROPDOWN_QUANTITY - 1
    : Math.max(1, Math.min(Math.floor(max), MAX_DROPDOWN_QUANTITY));

  return Array.from({ length: ceiling }, (_, index) => index + 1);
};

/**
 * Whether a quantity belongs in the text field rather than the dropdown. Once
 * `6+` owns the ceiling, everything from 6 up is typed instead of picked.
 */
export const usesCustomField = (quantity: number, max: number): boolean =>
  offersCustomQuantity(max) && quantity >= MAX_DROPDOWN_QUANTITY;

/**
 * Reads a typed quantity, ignoring anything that is not a digit. Returns null
 * for input that carries no number, so the caller can abandon the edit rather
 * than guess at an intent.
 */
export const parseQuantity = (raw: string): number | null => {
  const digits = raw.replace(/[^0-9]/g, '');

  if (!digits) {
    return null;
  }

  const parsed = Number(digits);

  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Holds a quantity inside `1..max`. Typing 20 against a max of 12 settles on
 * 12 rather than rejecting the entry.
 */
export const clampQuantity = (value: number, max: number): number => {
  const ceiling = Math.max(1, Math.floor(max));

  return Math.min(Math.max(Math.floor(value), 1), ceiling);
};

/**
 * Whether a typed draft is worth committing. Compares the raw entry rather than
 * the clamped one, so an over-max value stays committable — pressing Update is
 * what snaps the field down to the max the shopper can actually have.
 */
export const canUpdateQuantity = (draft: string, quantity: number): boolean => {
  const parsed = parseQuantity(draft);

  return parsed !== null && parsed !== quantity;
};
